import { LogViewer } from "@/components/LogViewer";
import { BackButton } from "@/components/environments/BackButton";
import { EnvironmentDetailsHeader } from "@/components/environments/EnvironmentDetailsHeader";
import { useStreamLogs } from "@/hooks/use-stream-logs";
import { useGetTask, useGetTaskExecution } from "@/queries/automations-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { type FC } from "react";
import { useParams } from "react-router-dom";
import { Text } from "@/components/podkit/typography/Text.tsx";
import { EnvironmentTaskRunStatus } from "@/components/environments/EnvironmentTaskRunStatus.tsx";
import { EnvironmentTaskRunActions } from "@/components/environments/EnvironmentTaskRunActions.tsx";
import { canOpen } from "@/routes/environments/phase";
import { SkeletonText } from "@/components/podkit/loading/Skeleton";
import { useEnvironmentDetailsOutletContext } from "@/hooks/use-environment-details-outlet-context";

export const EnvironmentTaskRunLogsPage: FC = () => {
    // Title is handled by EnvironmentDetailsLayout using dynamic title

    const { environmentId, taskId, runId } = useParams();
    const outletContext = useEnvironmentDetailsOutletContext();
    const showingAgentConversation = outletContext?.showingAgentConversation ?? false;

    const { data: environment } = useEnvironment(environmentId);
    const { data: taskExecution } = useGetTaskExecution(runId);
    const { data: task } = useGetTask(taskId);

    const logUrl = taskExecution?.status?.logUrl;
    const {
        logGroups,
        error: logsError,
        loadingMessage,
    } = useStreamLogs({
        environment,
        logsUrl: logUrl,
        maxLinesPerGroup: 10000,
    });

    const disabled = !environment || !canOpen(environment);

    return (
        <div className="flex size-full flex-col">
            {environmentId && taskId && (
                <div className="flex flex-col gap-4">
                    {!showingAgentConversation && <EnvironmentDetailsHeader showActionBar={false} />}
                    <div className="flex items-center justify-between gap-3 pb-4">
                        <BackButton showLabel={false} className="size-10 rounded-[20px] p-0" />
                        <div className="flex w-full min-w-0 items-center justify-between gap-2 rounded-xl border border-border-base bg-surface-glass p-3">
                            <div className="flex grow items-center gap-2 truncate">
                                <div className="flex w-full min-w-0 flex-col">
                                    <SkeletonText size="base" ready={!!task} className="w-40">
                                        <Text className="truncate text-base font-bold text-content-primary">
                                            {task?.metadata?.name}
                                        </Text>
                                    </SkeletonText>
                                    <SkeletonText size="sm" ready={!!task} className="w-40">
                                        <Text className="truncate text-sm text-content-secondary">
                                            {task?.metadata?.description}
                                        </Text>
                                    </SkeletonText>
                                </div>
                            </div>
                            {taskExecution && task && (
                                <>
                                    <EnvironmentTaskRunActions
                                        taskExecution={taskExecution}
                                        task={task}
                                        environmentId={environmentId}
                                        disabled={disabled}
                                    />
                                    <EnvironmentTaskRunStatus taskExecution={taskExecution} />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <LogViewer
                logGroups={logGroups}
                error={logsError}
                loadingMessage={loadingMessage}
                filterLogLinesWithoutTimestamps={true}
            />
        </div>
    );
};
